import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const JournalExpensesOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, journals, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const journalIds = new Set(
            (journals || []).map((j: any) => j.id as string)
        );

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const day = finance.dailyBreakdown![date];
            let journalExpense = 0;

            if (day?.breakdown) {
                for (const item of day.breakdown) {
                    if (item.type !== 'EXPENSE') continue;
                    const entityId = item.entityId as string;
                    const channelId = item.channelId as string;
                    const isJournalItem =
                        entityId?.startsWith('spec-') ||
                        entityId?.startsWith('journal-') ||
                        journalIds.has(channelId);

                    if (isJournalItem) {
                        journalExpense += Math.abs(Number(item.value) || 0);
                    }
                }
            }

            return {
                date,
                formattedDate: format(parseISO(date), 'MMM dd'),
                expense: journalExpense,
            };
        });
    }, [finance?.dailyBreakdown, journals]);

    const isEmpty = useMemo(
        () => chartData.length === 0 || chartData.every((d) => d.expense === 0),
        [chartData]
    );

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.journalExpenses', 'Journal Expenses'),
                            ]}
                            labelFormatter={(label: string) => label}
                        />
                        <Line
                            type="monotone"
                            dataKey="expense"
                            stroke={CHART_COLORS.chart.line5}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.chart.line5 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
